import React, {useEffect, useState} from 'react';
import {
  app,
  dialog,
  fs,
  fse,
  ipcRenderer,
  isDevMode,
  storageDirectoryPath
} from './electron-modules';
import {store} from './redux/store';
import slash from 'slash';
import path from 'path';
import {
  AssetType,
  selectAllAssets,
  getCameraFileAssets,
  getOrthoAssets,
  getPointCloudAssets,
  getModelAssets
} from './redux/assets-slice';
import LocalScene from './viewer/js/projections';
import {PythonExecutable, trackToolUsage} from './executable';
import {useSelector} from 'react-redux';
import {
  Button,
  CircularProgress,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputLabel,
  ListItem,
  makeStyles,
  Switch,
  Theme,
  Typography,
  useTheme
} from '@material-ui/core';
import {
  AlertDialog,
  ArrowTooltip,
  FolderWithLabel,
  LinearProgressWithLabel
} from './components';
import {Asset} from './redux/assets-slice';
import sanitize from "sanitize-filename";
import { selectName } from './redux/project-slice';
import { toast } from './app';
import WarningIcon from '@material-ui/icons/Warning';
import { asyncTimeout, pathWithExtension, updateFromJSON } from "./utilities";
import { useTranslation } from 'react-i18next';

const processTimeout = 1000;
const assetsFolder = "assets";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    warning: {
      color: theme.palette.warning.main
    },
    pointWarning: {
      color: theme.palette.warning.main,
      marginBottom: "-5px",
      paddingLeft: "5px"
    },
    exportText: {
      padding: theme.spacing(1)
    },
    contentCenter: {
      textAlign: "center"
    },
    cancelProgress: {
      marginRight: theme.spacing(1)
    },
    cancelContent: {
      paddingBottom: theme.spacing(3)
    },
    paddingTop: {
      paddingTop: theme.spacing(3)
    },
    bulletList: {
      display: 'list-item'
    },
    listItem: {
      width: '100%',
      marginTop: theme.spacing(2)
    },
    switchLabel: {
      display: "inline-block",
      maxWidth: "calc(100% - 50px)"
    },
    switchElement: {
      float: "right"
    },
    outputFolder: {
      marginTop: theme.spacing(2)
    }
  }),
);

const initialState = {
  rootOutputPath: null,
  webOutputPath: null,
  assets: [] as Asset[],
  numAssets: 0,
  currentIndex: 0
};

export const StaticExporter = (props) => {
  const {exportModalOpen, setExportModalOpen} = props;

  const theme = useTheme();
  const classes = useStyles(theme);
  const {t} = useTranslation();

  const assets = useSelector(selectAllAssets);
  const orthoFiles = getOrthoAssets(assets);
  const pointClouds = getPointCloudAssets(assets);
  const camerasFiles = getCameraFileAssets(assets);
  const modelFiles = getModelAssets(assets);

  const projectName = useSelector(selectName);

  const [outputPath, setOutputPath] = useState(null);
  const [exportName, setExportName] = useState("");
  const [dataExportOpen, setDataExportOpen] = useState(false);
  const [runnable, setRunnable] = useState(false);
  const [singleFileOutput, setSingleFileOutput] = useState(true);
  const [successOpen, setSuccessOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [runningExe, setRunningExe] = useState(null);
  const [exportTopMessage, setExportTopMessage] = useState("");
  const [exportBottomMessage, setExportBottomMessage] = useState("");
  const [exportPercent, setExportPercent] = useState(0);
  const [exportAssets, setExportAssets] = useState<{
    rootOutputPath: string | null,
    webOutputPath: string | null
    assets: Asset[]
    numAssets: number
    currentIndex: number
  }>(initialState);

  const numClouds = pointClouds.length;
  const numCameras = camerasFiles.length;
  const numOrthos = orthoFiles.length;
  const numModels = modelFiles.length;

  const canExport = (numClouds > 0)
    || (numCameras > 0)
    || (numOrthos > 0)
    || (numModels > 0);

  const numRawPointCloud = pointClouds.filter(asset => {
    return (asset.type === AssetType.LAS)
      || (asset.type === AssetType.E57Points);
  }).length;

  const handleOpen = () => {
    setExportModalOpen(true);
  };

  const handleClose = () => {
    setExportModalOpen(false);
  };

  const onExportFinished = () => {
    setRunnable(false);
    setDataExportOpen(false);
  };

  const handleExportCancel = () => {
    onExportFinished();
    setCancelling(true);
    setNewRunningExe(null);
  };

  const setNewRunningExe = (exe) => {
    runningExe?.destroy();
    setRunningExe(exe);
  };

  const handleExport = () => {
    handleClose();
    exportToHtml();
  };

  const setExportMessage = (top = "", bottom = "") => {
    setExportTopMessage(top);
    setExportBottomMessage(bottom);
  };

  const getAssetIndex = (state, asset) => {
    const assetIds = state.assets.map(x => x.id);
    const index = assetIds.indexOf(asset.id);
    return index;
  };

  const createNewFolder = async (folderPath) => {
    await deleteFolder(folderPath);
    await fse.mkdirs(folderPath);
  };

  const deleteFolder = async (folderPath) => {
    const folderExists = fs.existsSync(folderPath);
    if (folderExists) {
      await fse.remove(folderPath);
    }
  };

  const moveCameraData = async (asset, dataPath) => {
    const imagesPath = path.join(dataPath, asset.id);

    await createNewFolder(imagesPath);

    const imagesData = asset.data;
    for (let i=0; i<imagesData.length; i++) {
      const imageData = imagesData[i];
      const imageName = path.basename(imageData.path);
      const newImagePath = path.join(imagesPath, imageName);

      if (fs.existsSync(imageData.path)) {
        await fse.copy(imageData.path, newImagePath);
      }
    };

    await asyncTimeout(processTimeout);
  };

  const moveStreamablePointsData = async (asset, dataPath) => {
    const inputPath = asset.path;
    const outputPath = path.join(dataPath, asset.id);
    await copyToFolder(inputPath, outputPath);
  };

  const moveRawPointcloud = async (asset, dataPath) => {
    const inputPath = asset.path;
    const pointPath = path.join(dataPath, asset.id);

    return new Promise((resolve) => {
      let commands = [
        "-p", "process_las",
        "--input_path", inputPath,
        "--output_folder", pointPath,
        "--single_file", singleFileOutput,
        "--units", LocalScene.dataProjectionUnits
      ];

      const exe = new PythonExecutable();
      setNewRunningExe(exe);

      exe.run({
        command: commands,
        saveLogs: true,
        onLogs: text => {
          setExportBottomMessage(text);
        },
        onClose: async () => {
          await asyncTimeout(processTimeout);
          resolve(true);
        }
      });
    });
  };

  const moveModelData = async (asset, dataPath) => {
    const outputFolder = path.join(dataPath, asset.id);
    const modelName = path.basename(asset.path);

    await createNewFolder(outputFolder);

    if (asset.type === AssetType.SHP) {
      const shpInput = pathWithExtension(asset.path, "shp");
      const shpOutput = path.join(outputFolder, pathWithExtension(modelName, "shp"));
      await copyToFile(asset, shpInput, shpOutput);

      const dbfInput = pathWithExtension(asset.path, "dbf");
      const dbfOutput = path.join(outputFolder, pathWithExtension(modelName, "dbf"));
      await copyToFile(asset, dbfInput, dbfOutput);
    } else {
      const inputPath = asset.path;
      const outputPath = path.join(outputFolder, modelName);
      await copyToFile(asset, inputPath, outputPath);
    }
  };

  const copyToFile = async (asset, inputPath, outputPath) => {
    if (fs.existsSync(inputPath)) {
      await fse.copy(inputPath, outputPath);
    } else {
      dialog.showErrorBox(t("dialog.general-error-title"),
        t("dialog.export-generic", {name: asset.name}));
      return;
    }

    await asyncTimeout(processTimeout);
  };

  /** Execute python function to copy folders instead. Nodejs is
   * very slow and has issues with large files */
  const copyToFolder = async (inputPath, outputPath) => {
    return new Promise((resolve) => {
      let commands = [
        "-p", "copy_folder",
        "--input_folder", inputPath,
        "--output_folder", outputPath
      ];

      const exe = new PythonExecutable();
      setNewRunningExe(exe);

      exe.run({
        command: commands,
        onClose: async () => {
          await asyncTimeout(processTimeout);
          resolve(true);
        }
      });
    });
  };

  const updateCameraDataState = (asset, state) => {
    const assetIndex = getAssetIndex(state, asset);
    const imagesData = asset.data;
    imagesData.forEach((imageData, imageIndex) => {
      const imageName = path.basename(imageData.path);
      const finalPath = path.join(assetsFolder, asset.id, imageName);
      state.assets[assetIndex].data[imageIndex].path = `./${finalPath}`;
    });
  };

  const updateModelDataState = (asset, state) => {
    const assetIndex = getAssetIndex(state, asset);
    const modelName = path.basename(asset.path);
    const finalPath = path.join(assetsFolder, asset.id, modelName);
    state.assets[assetIndex].path = `./${finalPath}`;
  };

  const updateStreamablePointsState = (asset, state) => {
    const assetIndex = getAssetIndex(state, asset);
    const finalPath = path.join(assetsFolder, asset.id);
    state.assets[assetIndex].path = `./${finalPath}`;
  };

  const updateRawPointsState = (asset, state) => {
    const assetIndex = getAssetIndex(state, asset);

    const finalPath = singleFileOutput
      ? path.join(assetsFolder, asset.id)
      : path.join(assetsFolder, asset.id, "points");

    state.assets[assetIndex].path = `./${finalPath}`;
    state.assets[assetIndex].type = singleFileOutput
      ? AssetType.Potree
      : AssetType.Encompass;
  };

  const updateCameraTransformPath = (state, outputPath) => {
    const dataPath = path.join(outputPath, assetsFolder);

    const outputName = "camera.4x4";
    const matrixPath = state.camera.transform;
    const newImagePath = path.join(dataPath, outputName);

    if (matrixPath) {
      fse.copy(matrixPath, newImagePath);
      const finalPath = path.join(assetsFolder, outputName);
      state.camera.transform = `./${finalPath}`;
    }
  };

  const isModelFile = (asset) => {
    return (asset.type === AssetType.IFC)
      || (asset.type === AssetType.DXF)
      || (asset.type === AssetType.SHP);
  };

  const isStreamablePointsFile = (asset) => {
    return (asset.type === AssetType.Encompass)
       || (asset.type === AssetType.Potree);
  };

  const isRawPointsFile = (asset) => {
    return (asset.type === AssetType.LAS)
      || (asset.type === AssetType.E57Points);
  };

  const moveAssetData = async (asset, outputPath) => {
    const dataPath = path.join(outputPath, assetsFolder);

    if (isStreamablePointsFile(asset)) {
      await moveStreamablePointsData(asset, dataPath);
    } else if (isRawPointsFile(asset)) {
      await moveRawPointcloud(asset, dataPath);
    } else if (asset.type === AssetType.Panoramic) {
      await moveCameraData(asset, dataPath);
    } else if (asset.type === AssetType.Planar) {
      await moveCameraData(asset, dataPath);
    } else if (isModelFile(asset)) {
      await moveModelData(asset, dataPath);
    }
  };

  const finishExport = async (webOutputPath, rootOutputPath) => {
    setExportMessage(t('export.export-project-state'));

    // Get a deep copy of the project state
    let state = JSON.parse(JSON.stringify(store.getState()));

    // Update the fromJSON state
    state = updateFromJSON(state);

    const assets = exportAssets.assets;

    assets.forEach(asset => {
      if (isStreamablePointsFile(asset)) {
        updateStreamablePointsState(asset, state);
      } else if (isRawPointsFile(asset)) {
        updateRawPointsState(asset, state);
      } else if (asset.type === AssetType.Panoramic) {
        updateCameraDataState(asset, state);
      } else if (asset.type === AssetType.Planar) {
        updateCameraDataState(asset, state);
      } else if (isModelFile(asset)) {
        updateModelDataState(asset, state);
      }
    });

    // Update camera transform path
    updateCameraTransformPath(state, webOutputPath);

    setExportPercent(100);

    // Copy miniviewer.exe
    const miniViewerPath = getMiniViewerPath();
    const exeOutputPath = path.join(rootOutputPath, path.basename(miniViewerPath));
    fs.copyFileSync(miniViewerPath, exeOutputPath);

    // Copy global settings
    const globalSettingsPath = path.join(
      storageDirectoryPath, "global-settings.json");
    const globalSettingsOutput = path.join(
      webOutputPath, "global-settings.json");
    if (fs.existsSync(globalSettingsPath)) {
      fs.copyFileSync(globalSettingsPath, globalSettingsOutput);
    }

    // Write final config file
    const projectJson = JSON.stringify(state, null, 2);
    const projectPath = path.join(webOutputPath, "static.json");

    try {
      await fse.writeFile(projectPath, projectJson);
      await asyncTimeout(processTimeout);

      setDataExportOpen(false);
      setNewRunningExe(null);
      setSuccessOpen(true);
    } catch(err) {
      console.error(err);
      dialog.showErrorBox(t("dialog.general-error-title"),
        t("dialog.export-static")
      );
    }
  };

  const exportToHtml = async () => {
    const buildPath = getBuildPath();

    trackToolUsage("exporter");

    setExportPercent(0);
    setDataExportOpen(true);
    setExportMessage(t('export.creating-output-folder'));

    // Create final output folder
    const rootOutputPath = path.join(outputPath, exportName);
    await createNewFolder(rootOutputPath);

    // Create final output folder
    const webOutputPath = path.join(rootOutputPath, "web");
    await createNewFolder(webOutputPath);

    setExportMessage(t('export.copy-html-files-output-folder'));

    try {
      // Copy built website (html, js, etc..) files
      await fse.copy(buildPath, webOutputPath);
    } catch(err) {
      console.error(err);
      dialog.showErrorBox(t("dialog.general-error-title"),
        t("dialog.export-static"));
      return;
    }

    setExportMessage(t('export.preparing-data-to-export'));

    await asyncTimeout(processTimeout);

    const dataPath = path.join(webOutputPath, assetsFolder);
    await createNewFolder(dataPath);

    const assets = [
      ...pointClouds,
      ...camerasFiles,
      ...modelFiles
    ];

    console.log("Assets to export:", assets);

    if (assets.length === 0) {
      // Skip directly to final step
      return finishExport(webOutputPath, rootOutputPath);
    }

    setExportAssets({
      webOutputPath: webOutputPath,
      rootOutputPath: rootOutputPath,
      assets: assets,
      numAssets: assets.length,
      currentIndex: 0
    });

    setRunnable(true);
  };

  const getBuildPath = () => {
    const appPath = slash(app.getAppPath());
    const buildPath = isDevMode
      ? path.join(appPath, "build")
      : path.join(path.dirname(appPath), "app.asar.unpacked", "build");

    return buildPath;
  };

  const getMiniViewerPath = () => {
    const appPath = slash(app.getAppPath());
    const buildPath = isDevMode
      ? path.join(appPath, "miniviewer.exe")
      : path.join(path.dirname(appPath), "app.asar.unpacked", "miniviewer.exe");

    return buildPath;
  };

  const onCancel = async () => {
    await asyncTimeout(processTimeout);

    // Delete output folder and close modal
    const webOutputPath = exportAssets.webOutputPath;
    await deleteFolder(webOutputPath);
    setCancelOpen(false);
  };

  useEffect(() => {
    ipcRenderer?.removeAllListeners("open-static-export");
    ipcRenderer?.on("open-static-export", () => {
      if (canExport) {
        handleOpen();
        return;
      }

      toast.error(t("toast.no-data-loaded"));
    });
  }, [ipcRenderer, canExport]);

  useEffect(() => {
    setExportName(t('export.export-name', {
      name: t(sanitize(projectName))
    }));
  }, [projectName]);

  useEffect(() => {
    const currentIndex = exportAssets.currentIndex;
    const numAssets = exportAssets.numAssets;
    const webOutputPath = exportAssets.webOutputPath;
    const assets = exportAssets.assets;
    const toProcess = assets[currentIndex];

    if (!toProcess) {
      return;
    }

    if (!runnable) {
      return;
    }

    // Process one asset from the list
    const processNextAsset = async () => {
      setExportTopMessage(t("export.processing-data", {
        name: toProcess.name
      }));

      await moveAssetData(toProcess, webOutputPath);

      const nextIndex = currentIndex + 1;
      const percent = 100.0 * nextIndex / numAssets;

      setExportPercent(percent);
      setExportAssets({
        ...exportAssets,
        currentIndex: nextIndex
      });

      if (nextIndex === numAssets) {
        setFinishing(true);
      } else {
        setCancelling(false);
      }
    };
    processNextAsset();
  }, [exportAssets, runnable]);

  useEffect(() => {
    if (cancelling) {
      setCancelOpen(true);
    } else {
      onCancel();
    }
  }, [cancelling]);

  useEffect(() => {
    if (!finishing) {
      return;
    }

    if (!cancelling) {
      // Reached the end and not cancelling export
      const webOutputPath = exportAssets.webOutputPath;
      const rootOutputPath = exportAssets.rootOutputPath;
      finishExport(webOutputPath, rootOutputPath);
    }

    setCancelling(false);
    setFinishing(false);
  }, [finishing]);

  useEffect(() => {
    if (!exportModalOpen) return;
    setOutputPath(null);
    setSingleFileOutput(true);
  }, [exportModalOpen]);

  return (
    <React.Fragment>
      {/* Exporting dialog */}
      <Dialog
        open={dataExportOpen}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t('export.exporting-html')}</DialogTitle>

        <DialogContent className={classes.contentCenter}>
          <Typography variant="body1" className={classes.exportText}>
            {exportTopMessage}
          </Typography>

          <Typography variant="body2" className={classes.exportText}>
            {exportBottomMessage}
          </Typography>

          <LinearProgressWithLabel value={exportPercent} color="secondary"/>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleExportCancel}
            color="primary"
            disabled={(runnable === false) || (exportPercent === 100)}
          >
            {t('export.cancel-export')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Main dialog */}
      <Dialog
        fullWidth
        maxWidth="xs"
        open={exportModalOpen}
        onClose={handleClose}
      >
        <DialogTitle>{t('export.data-export')}</DialogTitle>

        <DialogContent>
          <Typography dangerouslySetInnerHTML={{
            __html: t("export.folder-text", {
              name: exportName
            })
          }}/>

          {/** TODO: add yellow triangle with export warning for points if needed */}

          {(numClouds > 0) && (<ListItem className={classes.bulletList}>
            {t("export.asset-points", {
              count: numClouds
            })}

            {(numRawPointCloud > 0) && (<ArrowTooltip
              title={t('export.this-process-may-take-some-time')}
            >
              <WarningIcon className={classes.pointWarning} fontSize="small"/>
            </ArrowTooltip>)}

          </ListItem >)}

          {(numCameras > 0) && (<ListItem className={classes.bulletList}>
            {t("export.asset-camera", {
              count: numCameras
            })}
          </ListItem>)}

          {(numOrthos > 0) && (<ListItem className={classes.bulletList}>
            {t("export.asset-orthos", {
              count: numOrthos
            })}
          </ListItem>)}

          {(numModels > 0) && (<ListItem className={classes.bulletList}>
            {t("export.asset-models", {
              count: numModels
            })}
          </ListItem>)}

          {/* Points render toggle */}
          {(numRawPointCloud > 0) && (<React.Fragment>
            <ListItem disableGutters>
              <div className={classes.listItem}>
                <InputLabel className={classes.switchLabel}>
                  {t('export.streamable_cloud_version_2_0')}:
                </InputLabel>

                <Switch
                  checked={singleFileOutput}
                  className={classes.switchElement}
                  size="small"
                  color="secondary"
                  onChange={(_, checked) => {
                    setSingleFileOutput(checked);
                  }}
                />
              </div>
            </ListItem>

            <Typography variant="caption">
              {t('export.the_newest_version_of_the_streamable_point_cloud')} </Typography>
          </React.Fragment>)}

          <div className={classes.outputFolder}>
            <FolderWithLabel
              title={t('export.output_folder')}
              label={t('export.the_output_folder_where_all_converted_data_will_be_created')}
              outputPath={outputPath}
              setOutputPath={setOutputPath}
            />
          </div>

        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="primary">
            {t("buttons.cancel")}
          </Button>

          <Button
            onClick={handleExport}
            color="primary"
            disabled={!outputPath}
          >
            {t("buttons.export")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancelling dialog */}
      <Dialog
        open={cancelOpen}
      >
        <DialogTitle>{t('export.data-export')}</DialogTitle>
        <DialogContent className={classes.cancelContent}>
          <CircularProgress size="1em" className={classes.cancelProgress}/>
          <DialogContentText display="inline">
            {t('export.cancelling-export')}
          </DialogContentText>
        </DialogContent>
      </Dialog>

      {/* Success dialog */}
      <AlertDialog
        open={successOpen}
        title={t('export.data-export')}
        handleClose={() => {
          setSuccessOpen(false);
        }}
      >
        <DialogContentText>
          {t('export.successfully-exported-data-to-static-html')}
        </DialogContentText>
      </AlertDialog>
    </React.Fragment>
  );
};
